// Typed URL search params for the sales routes (Module 09 §4.1, §4.5, §4.7;
// wave 1b, D-242; saved views, D-305).
//
// THE THREE BOOKS KEEP THEIR WHOLE STATE IN THE ADDRESS (PR-E4, D-305 rulings
// 3 and 4). Wave 1b put the sort in the URL and left the filters in component
// state, on the argument that a filtered customer list was not an address
// worth sharing. Saved views retired that argument: D-243 defines a view as a
// snapshot of URL state, so a filter that lives in component state is a
// filter no view can store and no link can carry. Every filter now lives in
// its route's `validateSearch`, beside `order` and the new `view`:
//
//   customers  view, q, tier, status, owner, credit_hold, needs_next_step,
//              tag, order, peek
//   quotes     view, customer, status, origin, owner, off_list,
//              created_from, created_to, order, peek
//   orders     view, customer, status, facility, order, peek, import_job
//
// `peek` (P3-E2 PR E2-1, D-314 ruling 8) is the side peek's address,
// `<kind>:<uuid>`, read through the peek's own codec
// (`components/peek-address.ts`). It is screen state outside every view: the
// saved-views layer keeps it through every write because it is not a filter
// key, and no view stores it. `needs_next_step` (D-314 ruling 9) filters the
// customer book on the discipline flag, and it is a filter of the
// `sales.customers` view grammar, so a saved view stores it as `true` and
// applying a view that does not name it clears it (spec 09 §4.1's E2-1
// annotation). `tag` (P3-E2 PR E2-2a1, TF1, D-323 ruling 6) filters the
// customer book on one tag, sent as `tag_id`, and a saved view stores the
// tag's id and never its name. An archived tag still filters, so the address
// keeps a tag id whatever the tag list says about it, and the screen names it
// "Not in this list" when the list it offers cannot name it.
//
// The filter keys are the contracts registry's (`SAVED_VIEW_SCREENS` in
// packages/contracts/src/saved-views/screens.ts, restated for the web as
// `SAVED_VIEW_SCREENS_WEB` in areas/office/views/lib/screens.ts), and each
// search type below is compile-time pinned to the generated config type of
// its screen in both directions, so a filter the contract adds or drops fails
// tsc here. A value has the config's shape: `credit_hold` and `off_list` are
// booleans, because the router reads `?credit_hold=true` as one.
//
// A DEFAULT IS AN ABSENT PARAMETER. Every validator returns `undefined` for a
// filter that is not set, the screens write a filter or sort set back to its
// default as an absent key, and they write their addresses through
// `compactSearch`, so a bare `/sales/quotes` is the address it always was and
// a reset filter leaves nothing behind. The declared default ORDER token is
// still accepted when an address spells it: while a view is active the
// order-token rule writes it explicitly (spec 16 §3.12 item 5), and an address
// of `view` and `order=-created_at` must not read as `view` alone.
//
// Validation is defensive by design (the production/inventory house pattern):
// an unknown or malformed value is DROPPED, never thrown on, and a shared link
// with a stale parameter still opens the screen. The shapes follow the list
// routes' own query schemas, so a value the server would refuse 400 is
// dropped here before any request exists: an id is a uuid (`z.guid()`
// on every id filter), a date is a calendar date, and `q` is 1 to 200
// characters. Strictness still lives server-side (D-142), and every mirror
// below is pinned to the generated client.
//
// A stored view's filters pass through these same validators when a view is
// applied (spec 16 §3.12 item 7): a stored value a validator drops is stale,
// and the switcher says so. That is why the date filters accept a calendar
// date only. The route also takes an instant, but the screen's date control
// cannot show one, and a filter the screen cannot show is a filter nobody can
// see or clear. `off_list` is accepted as `true` only for the same reason: the
// screen's control is §4.5's off-list-only toggle, which has no way to show
// `false`, although the route reads it as "no off-list lines".

import type { components } from "@assessly/api-client";
import { peekSearchParam } from "../../../../components/peek-address";
import { VIEW_SEARCH_TEXT_MAX, viewIdParam } from "../../views/lib/screens";
import { isValueOrderToken, type ValueSortVerdict } from "../lib/value-order";

// ---------------------------------------------------------------------------
// The shared grammar
// ---------------------------------------------------------------------------

/** A uuid, loosely: the shape the platform mints (v7) without pinning the
 * version, which is what the list routes' `z.guid()` accepts. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

/** The customer route's `q` cap, and the saved-view contract's: the one
 * constant every adopting screen's search box and validator share (R7). */
export const SALES_SEARCH_TEXT_MAX = VIEW_SEARCH_TEXT_MAX;

const CALENDAR_DATE_RE = /^\d{4}-\d{2}-\d{2}$/u;

function uuidParam(value: unknown): string | undefined {
  return typeof value === "string" && UUID_RE.test(value) ? value : undefined;
}

function oneOf<T extends string>(values: readonly T[], value: unknown): T | undefined {
  return typeof value === "string" && (values as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

/** A two-value filter. The router reads `?credit_hold=true` as a boolean; the
 * quoted string spelling a hand-written link can carry is accepted too. */
function boolParam(value: unknown): boolean | undefined {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return undefined;
}

/** Free text for a search box: 1 to 200 characters that are not all blank,
 * with no NUL character, which Postgres text cannot hold. A number the
 * router parsed out of a hand-typed `?q=123` is dropped rather than turned
 * back into text: `?q=1e5` would come back as `100000`. */
function searchText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  if (value.trim().length === 0 || value.length > SALES_SEARCH_TEXT_MAX) return undefined;
  return value.includes("\u0000") ? undefined : value;
}

/** A calendar date `YYYY-MM-DD` that exists, the value a date input writes. */
function calendarDate(value: unknown): string | undefined {
  if (typeof value !== "string" || !CALENDAR_DATE_RE.test(value)) return undefined;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const probe = new Date(0);
  probe.setUTCFullYear(year, month - 1, day);
  return probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
    ? value
    : undefined;
}

/**
 * The address a screen writes: the search with every absent member removed,
 * so a filter set back to its default leaves no key behind and the router is
 * handed exactly the parameters the address holds. `false` is a value and
 * stays (`credit_hold=false` is a filter the customer book honours).
 */
export function compactSearch<T extends object>(search: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(search)) {
    if (value !== undefined) out[key] = value;
  }
  return out as T;
}

/**
 * The both-ways pin between a screen's search type `S` and its saved-view
 * filter config `F` (the generated `SavedView…Config["filters"]`). A record
 * typed with it must name every config key and no other, and each member is
 * `true` only when `S` declares that key with a value the config accepts. A
 * filter the contract adds, drops or reshapes therefore fails tsc here, and a
 * Save as new built from the address can never send a shape the server
 * refuses.
 */
type FilterKeyPin<S, F> = {
  readonly [K in keyof F]-?: K extends keyof S
    ? [NonNullable<S[K]>] extends [NonNullable<F[K]>]
      ? true
      : never
    : never;
};

/**
 * D-301: a money order (`±total_minor`) the reader is KNOWN not to be allowed
 * is dropped here, before a request exists, exactly like an unknown token.
 * The router calls these validators with the search alone, so the default
 * verdict is `unknown` and the token survives into the address; the screen
 * validates again with the reader's verdict (lib/value-order.ts), and only
 * `refused` drops it. The server refuses the order 403 either way. Since D-301
 * round 7 the screen then takes the dropped token out of the address, and
 * since PR-E4 it does so through the saved-views controller, which removes
 * `order` alone and keeps `view` and every filter (D-305 ruling 7).
 */
function orderAllowed(token: string, valueSort: ValueSortVerdict): boolean {
  return !(valueSort === "refused" && isValueOrderToken(token));
}

// ---------------------------------------------------------------------------
// §4.1 CUSTOMER BOOK
// ---------------------------------------------------------------------------

/** The D-242 wire tokens of `GET /v1/customers` (`-` prefix = desc). */
export type CustomersOrderToken = components["schemas"]["CustomersOrder"];

/** The server-applied default (D-242.3). The URL carries `order` only when it
 * differs, so a default-sorted address stays byte-identical to the pre-sort
 * one every certified walkthrough visits. */
export const CUSTOMERS_DEFAULT_ORDER: CustomersOrderToken = "-created_at";

// `Record<CustomersOrderToken, true>` is the both-ways pin: a contract enum
// member missing here fails tsc, and a token lingering here after leaving the
// contract fails the moment the client regenerates. Every WIRE token is URL-
// legal, `updated_at` included: since PR-E4 it has a caret on the Updated
// column a reader shows through the chooser (spec 09 §4.1).
const CUSTOMERS_ORDER_TOKENS: Record<CustomersOrderToken, true> = {
  "-created_at": true,
  created_at: true,
  display_name: true,
  "-display_name": true,
  customer_no: true,
  "-customer_no": true,
  updated_at: true,
  "-updated_at": true,
  reliability_score: true,
  "-reliability_score": true,
};

export function isCustomersOrderToken(value: unknown): value is CustomersOrderToken {
  return typeof value === "string" && Object.hasOwn(CUSTOMERS_ORDER_TOKENS, value);
}

type CustomersViewFilters = components["schemas"]["SavedViewCustomersConfig"]["filters"];

export const CUSTOMER_TIERS = [
  "market",
  "b2b",
  "distributor",
] as const satisfies readonly NonNullable<CustomersViewFilters["tier"]>[];
export const CUSTOMER_STATUSES = ["active", "archived"] as const satisfies readonly NonNullable<
  CustomersViewFilters["status"]
>[];

export interface SalesCustomersSearch {
  /** The saved view the address names (spec 16 §3.12 item 5). */
  readonly view?: string | undefined;
  /** The search box: name, number, tax ID, and a contact address for a
   * holder of `messaging.address.read` (D-307). */
  readonly q?: string | undefined;
  readonly tier?: NonNullable<CustomersViewFilters["tier"]> | undefined;
  readonly status?: NonNullable<CustomersViewFilters["status"]> | undefined;
  /** The owning user's id, sent as `owner_user_id`. */
  readonly owner?: string | undefined;
  readonly credit_hold?: boolean | undefined;
  /** The discipline flag (§3.12): `true` or absent, sent as
   * `needs_next_step=true`. A view filter since E2-1 (D-314 ruling 9). */
  readonly needs_next_step?: true | undefined;
  /** One tag's id, sent as `tag_id` (TF1, D-323 ruling 6). */
  readonly tag?: string | undefined;
  readonly order?: CustomersOrderToken | undefined;
  /** The side peek, `<kind>:<uuid>`; never a filter. */
  readonly peek?: string | undefined;
}

// The screen's URL filter keys, pinned to its view config (`FilterKeyPin`).
const CUSTOMERS_FILTER_KEYS: FilterKeyPin<SalesCustomersSearch, CustomersViewFilters> = {
  q: true,
  tier: true,
  status: true,
  owner: true,
  credit_hold: true,
  needs_next_step: true,
  tag: true,
};
export const SALES_CUSTOMERS_FILTER_KEYS = Object.keys(
  CUSTOMERS_FILTER_KEYS,
) as readonly (keyof CustomersViewFilters)[];

export function validateSalesCustomersSearch(
  search: Record<string, unknown>,
): SalesCustomersSearch {
  const order = search["order"];
  return {
    view: viewIdParam(search["view"]),
    q: searchText(search["q"]),
    tier: oneOf(CUSTOMER_TIERS, search["tier"]),
    status: oneOf(CUSTOMER_STATUSES, search["status"]),
    owner: uuidParam(search["owner"]),
    credit_hold: boolParam(search["credit_hold"]),
    needs_next_step: boolParam(search["needs_next_step"]) === true ? true : undefined,
    tag: uuidParam(search["tag"]),
    order: isCustomersOrderToken(order) ? order : undefined,
    peek: peekSearchParam(search["peek"]),
  };
}

// ---------------------------------------------------------------------------
// §4.5 QUOTE BOOK (slice P3-C-1), the first list BORN with wave 1
// ---------------------------------------------------------------------------
//
// The CARET SET is narrower than the wire enum, and deliberately so: the
// contract's own header argues each exclusion (status/origin are raw enums
// under translated chips, `customer` is a cross-table display name,
// `revision_count` is derived). Every WIRE token is still URL-legal, including
// `updated_at`, which has no caret: the URL speaks the API's grammar, not the
// header row's subset of it.
//
// `customer` is how the customer 360's "all quotes for this customer" link
// addresses the book, and since PR-E4 it is also where the picker writes its
// choice, so the link and the picker set one state (D-305 ruling 4).

/** The D-242 wire tokens of `GET /v1/quotes`. */
export type QuotesOrderToken = components["schemas"]["QuotesOrder"];

/** The server-applied default (§5: "newest first by default"). */
export const QUOTES_DEFAULT_ORDER: QuotesOrderToken = "-created_at";

const QUOTES_ORDER_TOKENS: Record<QuotesOrderToken, true> = {
  "-created_at": true,
  created_at: true,
  quote_no: true,
  "-quote_no": true,
  updated_at: true,
  "-updated_at": true,
  valid_until: true,
  "-valid_until": true,
  total_minor: true,
  "-total_minor": true,
};

export function isQuotesOrderToken(value: unknown): value is QuotesOrderToken {
  return typeof value === "string" && Object.hasOwn(QUOTES_ORDER_TOKENS, value);
}

type QuotesViewFilters = components["schemas"]["SavedViewQuotesConfig"]["filters"];

export const QUOTE_STATUSES = [
  "draft",
  "pending_approval",
  "approved",
  "sent",
  "accepted",
  "converted",
  "rejected",
  "expired",
  "cancelled",
] as const satisfies readonly NonNullable<QuotesViewFilters["status"]>[];

export const QUOTE_ORIGINS = ["manual", "agent", "portal"] as const satisfies readonly NonNullable<
  QuotesViewFilters["origin"]
>[];

export interface SalesQuotesSearch {
  readonly view?: string | undefined;
  /** The customer, sent as `customer_id`: set by the 360's link or by the
   * picker, and cleared by either. */
  readonly customer?: string | undefined;
  readonly status?: NonNullable<QuotesViewFilters["status"]> | undefined;
  readonly origin?: NonNullable<QuotesViewFilters["origin"]> | undefined;
  /** The owning user's id, sent as `owner_user_id`. */
  readonly owner?: string | undefined;
  /** §4.5's off-list-only toggle: `true` or absent (the header says why). */
  readonly off_list?: true | undefined;
  readonly created_from?: string | undefined;
  readonly created_to?: string | undefined;
  readonly order?: QuotesOrderToken | undefined;
  /** The side peek, `<kind>:<uuid>`; never a filter. */
  readonly peek?: string | undefined;
}

const QUOTES_FILTER_KEYS: FilterKeyPin<SalesQuotesSearch, QuotesViewFilters> = {
  customer: true,
  status: true,
  origin: true,
  owner: true,
  off_list: true,
  created_from: true,
  created_to: true,
};
export const SALES_QUOTES_FILTER_KEYS = Object.keys(
  QUOTES_FILTER_KEYS,
) as readonly (keyof QuotesViewFilters)[];

export function validateSalesQuotesSearch(
  search: Record<string, unknown>,
  valueSort: ValueSortVerdict = "unknown",
): SalesQuotesSearch {
  const order = search["order"];
  return {
    view: viewIdParam(search["view"]),
    customer: uuidParam(search["customer"]),
    status: oneOf(QUOTE_STATUSES, search["status"]),
    origin: oneOf(QUOTE_ORIGINS, search["origin"]),
    owner: uuidParam(search["owner"]),
    off_list: boolParam(search["off_list"]) === true ? true : undefined,
    created_from: calendarDate(search["created_from"]),
    created_to: calendarDate(search["created_to"]),
    order: isQuotesOrderToken(order) && orderAllowed(order, valueSort) ? order : undefined,
    peek: peekSearchParam(search["peek"]),
  };
}

// ---------------------------------------------------------------------------
// §4.4 COMPOSER (new quote / new order)
// ---------------------------------------------------------------------------

export interface SalesComposerSearch {
  /**
   * Who the document is for, set by whoever LINKED here — the messaging
   * inbox's "Create quote" (spec 17 §4.1) opens the composer already pointed
   * at the conversation's customer, so the manager does not retype a name the
   * screen they came from already knew.
   *
   * It is a starting VALUE, not a lock: the picker stays editable, and an id
   * that resolves to nothing the caller may read leaves it simply empty. The
   * param is validated to uuid SHAPE and otherwise dropped — never thrown on,
   * the quote book's rule — because a malformed deep link should open an
   * ordinary blank composer rather than an error card.
   */
  readonly customer_id?: string | undefined;
  /**
   * The deal the new quote belongs to (P3-E2 PR E2-1, spec 09 §4.14): the
   * deal page's Create quote opens the composer with its customer and itself
   * named, and the composer's Deal control links the new quote at create.
   * A uuid or absent, like `customer_id`; a deal the reader cannot write
   * answers the create's 404, which the composer words.
   */
  readonly opportunity_id?: string | undefined;
}

export function validateSalesComposerSearch(search: Record<string, unknown>): SalesComposerSearch {
  return {
    customer_id: uuidParam(search["customer_id"]),
    opportunity_id: uuidParam(search["opportunity_id"]),
  };
}

// ---------------------------------------------------------------------------
// §4.7 ORDER LIST
// ---------------------------------------------------------------------------

/** The D-242 wire tokens of `GET /v1/orders`. Note what is NOT here:
 * `promise_date`. Module 03 is absent, every value is NULL, and a sort control
 * over a column of nulls is a control that cannot do anything (D-033) — the
 * contract omits the token for the same reason the screen omits the caret, so
 * the two cannot drift. It arrives with 03. */
export type OrdersOrderToken = components["schemas"]["OrdersOrder"];

/** §5's declared default: the order book's question is "what did we just take
 * on", so the newest CONFIRMED order leads — not the newest created. */
export const ORDERS_DEFAULT_ORDER: OrdersOrderToken = "-confirmed_at";

const ORDERS_ORDER_TOKENS: Record<OrdersOrderToken, true> = {
  "-confirmed_at": true,
  confirmed_at: true,
  order_no: true,
  "-order_no": true,
  created_at: true,
  "-created_at": true,
  total_minor: true,
  "-total_minor": true,
};

export function isOrdersOrderToken(value: unknown): value is OrdersOrderToken {
  return typeof value === "string" && Object.hasOwn(ORDERS_ORDER_TOKENS, value);
}

type OrdersViewFilters = components["schemas"]["SavedViewOrdersConfig"]["filters"];

export const ORDER_STATUSES = [
  "pending_approval",
  "confirmed",
  "in_production",
  "ready",
  "shipped",
  "delivered",
  "closed",
  "cancelled",
] as const satisfies readonly NonNullable<OrdersViewFilters["status"]>[];

export interface SalesOrdersSearch {
  readonly view?: string | undefined;
  /** The customer, sent as `customer_id`: §4.7's filter row names it, the
   * 360 and the duplicate-PO notice link to it (`/sales/orders?customer=`),
   * and the picker writes it. */
  readonly customer?: string | undefined;
  readonly status?: NonNullable<OrdersViewFilters["status"]> | undefined;
  /** The fulfilling facility's id, sent as `facility_id`. */
  readonly facility?: string | undefined;
  readonly order?: OrdersOrderToken | undefined;
  /** The side peek, `<kind>:<uuid>`; never a filter. */
  readonly peek?: string | undefined;
  /**
   * The open orders import that loaded the rows, sent as
   * `imported_from_job_id` (spec 09 §4.19). Set by the job page's link, shown
   * as a chip, and never stored in a view (D-359).
   */
  readonly import_job?: string | undefined;
  /**
   * The at-risk toggle, sent as `at_risk=true` (spec 09 §4.7, P3-J J-3): the
   * orders with a date passed on an unready line or a promise at risk or
   * broken. A URL filter only, like `import_job`: the saved-view config has
   * no slot for it (spec 16 §3.12), so a view never stores it.
   */
  readonly at_risk?: "1" | undefined;
}

const ORDERS_FILTER_KEYS: FilterKeyPin<SalesOrdersSearch, OrdersViewFilters> = {
  customer: true,
  status: true,
  facility: true,
};
export const SALES_ORDERS_FILTER_KEYS = Object.keys(
  ORDERS_FILTER_KEYS,
) as readonly (keyof OrdersViewFilters)[];

export function validateSalesOrdersSearch(
  search: Record<string, unknown>,
  valueSort: ValueSortVerdict = "unknown",
): SalesOrdersSearch {
  const order = search["order"];
  return {
    view: viewIdParam(search["view"]),
    customer: uuidParam(search["customer"]),
    status: oneOf(ORDER_STATUSES, search["status"]),
    facility: uuidParam(search["facility"]),
    order: isOrdersOrderToken(order) && orderAllowed(order, valueSort) ? order : undefined,
    peek: peekSearchParam(search["peek"]),
    import_job: uuidParam(search["import_job"]),
    // TanStack's search parser reads `?at_risk=1` as the number 1.
    at_risk: search["at_risk"] === "1" || search["at_risk"] === 1 ? "1" : undefined,
  };
}

// ---------------------------------------------------------------------------
// §4.2 CUSTOMER 360 — the TAB (slice P3-C-2, D-263)
// ---------------------------------------------------------------------------
//
// One URL param, because a LINK sets it: the §4.6 inbox's `price_override`
// card deep-links to the customer's Pricing tab — the surface the proposal
// sits on — and a link that lands the reviewer on the Overview with the tab
// strip still to be found is a deep link that does not deep-link. Switching
// tabs by hand stays component state (a reload returns to the Overview, the
// screen a manager opens mid-call), so a bare `/sales/customers/<id>` is the
// same address it always was.
//
// Validated defensively like every other param here: a foreign or malformed
// value is DROPPED, never thrown on, and the screen opens its default.

export const CUSTOMER_360_TABS = ["overview", "contacts", "pricing"] as const;
export type Customer360Tab = (typeof CUSTOMER_360_TABS)[number];

export function isCustomer360Tab(value: unknown): value is Customer360Tab {
  return typeof value === "string" && (CUSTOMER_360_TABS as readonly string[]).includes(value);
}

export interface SalesCustomer360Search {
  readonly tab?: Customer360Tab | undefined;
}

export function validateSalesCustomer360Search(
  search: Record<string, unknown>,
): SalesCustomer360Search {
  const tab = search["tab"];
  return { tab: isCustomer360Tab(tab) ? tab : undefined };
}
